// client/src/components/pages/AmendmentsAllocateTransactions/FollowSourceReportTab.tsx
//
// ST-00212: the Amendments page's "Source follow report" tab, for settings.manage only. The
// follow-source job's mode (AMENDMENT_FOLLOW_SOURCE = off | dry-run | on), its latest runs, a
// "Run dry run now" button, and the newest successful run's report: what the job would change
// (dry run) or changed — re-attached corrections, the review list by reason, retired corrections
// by field, the nett-weight change per route grouping and month, and per ticket. The founder
// signs the dry-run report off before the job is switched on in prod.
// GET /api/amendments/v3/follow-source/runs, POST /api/amendments/v3/follow-source/dry-run.
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { FaPlay, FaSpinner } from 'react-icons/fa';
import {
  followSourceErrorText,
  getFollowSourceRuns,
  runFollowSourceDryRun,
} from '../../../services/amendmentFollowSource';
import type {
  FollowSourceReport,
  FollowSourceRunSummary,
  FollowSourceRunsResponse,
  ReviewReason,
} from '../../../types/amendmentFollowSource';
import { formatAppDateTime, formatMiningDateTime } from '../../../utils/dateUtils';
import {
  EVENT_TYPE_LABEL,
  FOLLOW_SOURCE_MODE,
  REVIEW_REASONS,
  formatCount,
  formatTonnes,
  reviewReasonLabel,
} from './followSourceFormat';
import { BUTTON_PRIMARY, Banner, EmptyRow, TABLE, TBODY, TD, TH, THEAD } from './FollowSourceParts';

/** Per-ticket rows shown on the page (the report keeps up to 2,000). */
export const TICKETS_SHOWN = 200;
const RUNS_SHOWN = 10;

const TD_NUM = `${TD} text-right tabular-nums whitespace-nowrap`;
const TH_NUM = `${TH} text-right`;

function Tile({ label, value, detail }: { label: string; value: ReactNode; detail?: ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-surface px-4 py-3">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div className="mt-1 text-xl font-semibold tabular-nums text-slate-900">{value}</div>
      {detail && <div className="mt-0.5 text-[11px] text-slate-500">{detail}</div>}
    </div>
  );
}

function Section({
  title,
  note,
  children,
}: {
  title: string;
  note?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="mt-5">
      <h3 className="px-4 text-sm font-semibold text-slate-800">{title}</h3>
      {note && <p className="px-4 mt-0.5 text-xs text-slate-500">{note}</p>}
      <div className="mt-2 overflow-x-auto border-y border-slate-200">{children}</div>
    </section>
  );
}

function runStatus(run: FollowSourceRunSummary): ReactNode {
  if (run.error) return <span className="text-red-700">{run.error}</span>;
  if (!run.finishedAt) return <span className="text-slate-500">Running or stopped</span>;
  return <span className="text-slate-500">Finished</span>;
}

function ReportView({ report }: { report: FollowSourceReport }) {
  const t = report.totals;
  const reasons = REVIEW_REASONS.filter((r) => (report.orphanedByReason[r] ?? 0) > 0);
  const extraReasons = (Object.keys(report.orphanedByReason) as ReviewReason[]).filter(
    (r) => !REVIEW_REASONS.includes(r) && (report.orphanedByReason[r] ?? 0) > 0
  );
  const tickets = report.tickets.slice(0, TICKETS_SHOWN);
  const isDryRun = report.mode === 'dry-run';

  return (
    <div className="pb-5" data-testid="follow-source-report">
      <div className="px-4 pt-4">
        <h2 className="text-base font-semibold text-slate-900">
          {isDryRun ? 'What the job would change (dry run)' : 'What the job changed'}
        </h2>
        <p className="mt-0.5 text-xs text-slate-500">
          Report of {formatAppDateTime(report.generatedAt)}: {formatCount(t.events)} events on{' '}
          {formatCount(t.tickets)} tickets.{' '}
          {isDryRun
            ? 'Nothing was changed: this is what the job would do if it were switched on.'
            : 'These changes were made.'}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 px-4 mt-3 sm:grid-cols-3 xl:grid-cols-5">
        <Tile
          label="Re-attached"
          value={formatCount(t.reanchored)}
          detail="corrections moved to a re-issued ticket"
        />
        <Tile
          label="To review"
          value={formatCount(t.orphaned)}
          detail="go on the Unmatched corrections list"
        />
        <Tile
          label="Retired"
          value={formatCount(t.retired)}
          detail={`of which ${formatCount(t.retiredDifferent)} with another source value, ${formatCount(
            t.retiredAdopted
          )} where the source agrees`}
        />
        <Tile
          label="Nett change"
          value={formatTonnes(t.nettDelta, true)}
          detail={`${formatTonnes(t.nettBefore)} → ${formatTonnes(t.nettAfter)} on the touched tickets`}
        />
        <Tile
          label="Route moves"
          value={formatCount(t.routeMoves)}
          detail="tickets whose dispatch or receipt location changes"
        />
      </div>

      <div className="grid gap-x-6 lg:grid-cols-2">
        <Section title="Review list by reason">
          <table className={TABLE} aria-label="Review list by reason">
            <thead className={THEAD}>
              <tr>
                <th className={TH}>Reason</th>
                <th className={TH_NUM}>Corrections</th>
              </tr>
            </thead>
            <tbody className={TBODY}>
              {reasons.length + extraReasons.length === 0 ? (
                <EmptyRow colSpan={2}>Nothing goes on the review list.</EmptyRow>
              ) : (
                [...reasons, ...extraReasons].map((r) => (
                  <tr key={r}>
                    <td className={TD}>{reviewReasonLabel(r)}</td>
                    <td className={TD_NUM}>{formatCount(report.orphanedByReason[r])}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </Section>

        <Section title="Retired corrections by field">
          <table className={TABLE} aria-label="Retired corrections by field">
            <thead className={THEAD}>
              <tr>
                <th className={TH}>Field</th>
                <th className={TH_NUM}>Retired</th>
                <th className={TH_NUM}>Source agrees</th>
                <th className={TH_NUM}>Another value</th>
              </tr>
            </thead>
            <tbody className={TBODY}>
              {report.retiredByField.length === 0 ? (
                <EmptyRow colSpan={4}>No correction is retired.</EmptyRow>
              ) : (
                report.retiredByField.map((f) => (
                  <tr key={f.field}>
                    <td className={TD}>{f.field}</td>
                    <td className={TD_NUM}>{formatCount(f.retired)}</td>
                    <td className={TD_NUM}>{formatCount(f.adopted)}</td>
                    <td className={TD_NUM}>{formatCount(f.different)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </Section>
      </div>

      <Section
        title="Nett weight per route grouping and month"
        note="Only the groupings and months whose figures change, largest change first."
      >
        <table className={TABLE} aria-label="Nett weight per route grouping and month">
          <thead className={THEAD}>
            <tr>
              <th className={TH}>Route grouping</th>
              <th className={TH}>Month</th>
              <th className={TH}>Type</th>
              <th className={TH_NUM}>Tickets</th>
              <th className={TH_NUM}>Before → after</th>
              <th className={TH_NUM}>Change</th>
            </tr>
          </thead>
          <tbody className={TBODY}>
            {report.groupings.length === 0 ? (
              <EmptyRow colSpan={6}>No route grouping changes.</EmptyRow>
            ) : (
              report.groupings.map((g) => (
                <tr key={`${g.groupingId}-${g.month}-${g.ticketType}`}>
                  <td className={TD}>{g.groupingName}</td>
                  <td className={`${TD} tabular-nums whitespace-nowrap`}>{g.month}</td>
                  <td className={TD}>{g.ticketType}</td>
                  <td className={TD_NUM}>{formatCount(g.tickets)}</td>
                  <td className={TD_NUM}>
                    {formatTonnes(g.nettBefore)} → {formatTonnes(g.nettAfter)}
                  </td>
                  <td className={`${TD_NUM} font-medium text-slate-900`}>
                    {formatTonnes(g.nettDelta, true)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Section>

      <Section
        title="Per ticket"
        note={
          <>
            Largest nett change first.{' '}
            {report.tickets.length > TICKETS_SHOWN &&
              `Showing the first ${formatCount(TICKETS_SHOWN)} of ${formatCount(report.tickets.length)} tickets in the report. `}
            {report.ticketsTruncated &&
              'The report keeps only the tickets with the largest change; the totals above cover every ticket.'}
          </>
        }
      >
        <table className={TABLE} aria-label="Changes per ticket">
          <thead className={THEAD}>
            <tr>
              <th className={TH}>Ticket No</th>
              <th className={TH}>Weigh time</th>
              <th className={TH}>What happens</th>
              <th className={TH}>Reason</th>
              <th className={TH}>Fields</th>
              <th className={TH_NUM}>Nett before → after</th>
              <th className={TH}>Route before → after</th>
            </tr>
          </thead>
          <tbody className={TBODY}>
            {tickets.length === 0 ? (
              <EmptyRow colSpan={7}>No ticket changes.</EmptyRow>
            ) : (
              tickets.map((tk, i) => (
                <tr key={`${tk.oldUid ?? ''}-${tk.newUid ?? ''}-${i}`}>
                  <td className={`${TD} font-mono whitespace-nowrap`}>{tk.ticketNo || '—'}</td>
                  <td className={`${TD} tabular-nums whitespace-nowrap`}>
                    {formatMiningDateTime(tk.ticketTimestamp) || '—'}
                  </td>
                  <td className={TD}>
                    {[...new Set(tk.events.map((e) => EVENT_TYPE_LABEL[e] || e))].join(', ') || '—'}
                  </td>
                  <td className={TD}>{tk.reason ? reviewReasonLabel(tk.reason) : '—'}</td>
                  <td className={TD}>{tk.fields.length ? tk.fields.join(', ') : '—'}</td>
                  <td className={TD_NUM}>
                    {formatTonnes(tk.nettBefore)} → {formatTonnes(tk.nettAfter)}
                  </td>
                  <td className={TD}>
                    {tk.routeBefore === tk.routeAfter
                      ? tk.routeAfter || '—'
                      : `${tk.routeBefore || '—'} → ${tk.routeAfter || '—'}`}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Section>
    </div>
  );
}

const FollowSourceReportTab = () => {
  const [data, setData] = useState<FollowSourceRunsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const [runNotice, setRunNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getFollowSourceRuns(RUNS_SHOWN));
    } catch (err) {
      setError(followSourceErrorText(err, 'Could not load the follow-source runs.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runDryRun = async () => {
    setRunning(true);
    setRunError(null);
    setRunNotice(null);
    try {
      const run = await runFollowSourceDryRun();
      setRunNotice(
        `Dry run finished: ${formatCount(run.eventsCount ?? 0)} events on ${formatCount(
          run.ticketsCount ?? 0
        )} tickets, nett change ${formatTonnes(run.nettDelta ?? 0, true)}.`
      );
      await load();
    } catch (err) {
      setRunError(followSourceErrorText(err, 'The dry run failed.'));
    } finally {
      setRunning(false);
    }
  };

  const mode = data ? FOLLOW_SOURCE_MODE[data.mode] : null;
  const latest = data?.runs.find((r) => r.report) ?? null;

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3 px-4 py-3 border-b border-slate-200">
        <div className="text-xs text-slate-600">
          <div>
            <span className="font-medium text-slate-500 uppercase tracking-wider">Job mode</span>{' '}
            {mode ? (
              <span
                className="ml-1 inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700 ring-1 ring-inset ring-slate-200"
                data-testid="follow-source-mode"
              >
                {mode.label}
              </span>
            ) : (
              '—'
            )}
          </div>
          {mode && <p className="mt-1 max-w-2xl text-slate-500">{mode.help}</p>}
          <p className="mt-1 max-w-2xl text-slate-500">
            A dry run works out what the job would change today and keeps only its report. It never
            changes a ticket.
          </p>
        </div>
        <button type="button" className={BUTTON_PRIMARY} onClick={runDryRun} disabled={running}>
          {running ? (
            <FaSpinner className="w-3 h-3 animate-spin" aria-hidden="true" />
          ) : (
            <FaPlay className="w-3 h-3" aria-hidden="true" />
          )}
          {running ? 'Running the dry run…' : 'Run dry run now'}
        </button>
      </div>

      {error && <Banner tone="error">{error}</Banner>}
      {runError && (
        <Banner tone="error" onDismiss={() => setRunError(null)}>
          {runError}
        </Banner>
      )}
      {runNotice && (
        <Banner tone="success" onDismiss={() => setRunNotice(null)}>
          {runNotice}
        </Banner>
      )}
      {running && (
        <Banner tone="info">The dry run can take up to a minute. Keep this tab open.</Banner>
      )}

      <Section title="Latest runs">
        <table className={TABLE} aria-label="Latest runs">
          <thead className={THEAD}>
            <tr>
              <th className={TH}>Started</th>
              <th className={TH}>Mode</th>
              <th className={TH}>Trigger</th>
              <th className={TH}>By</th>
              <th className={TH_NUM}>Events</th>
              <th className={TH_NUM}>Tickets</th>
              <th className={TH_NUM}>Nett change</th>
              <th className={TH}>Status</th>
            </tr>
          </thead>
          <tbody className={TBODY}>
            {loading && !data ? (
              <EmptyRow colSpan={8}>
                <FaSpinner className="inline w-3.5 h-3.5 mr-1.5 animate-spin" aria-hidden="true" />
                Loading runs…
              </EmptyRow>
            ) : !data || data.runs.length === 0 ? (
              <EmptyRow colSpan={8}>The job has not run yet.</EmptyRow>
            ) : (
              data.runs.map((run) => (
                <tr key={run.runId}>
                  <td className={`${TD} tabular-nums whitespace-nowrap`}>
                    {formatAppDateTime(run.startedAt)}
                  </td>
                  <td className={TD}>{FOLLOW_SOURCE_MODE[run.mode]?.label ?? run.mode}</td>
                  <td className={TD}>{run.trigger}</td>
                  <td className={TD}>{run.requestedBy || '—'}</td>
                  <td className={TD_NUM}>{formatCount(run.eventsCount)}</td>
                  <td className={TD_NUM}>{formatCount(run.ticketsCount)}</td>
                  <td className={TD_NUM}>
                    {run.nettDelta === null ? '—' : formatTonnes(run.nettDelta, true)}
                  </td>
                  <td className={TD}>{runStatus(run)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Section>

      {latest?.report ? (
        <ReportView report={latest.report} />
      ) : (
        data && (
          <p className="px-4 py-6 text-xs text-slate-500">
            No report yet. Click <strong>Run dry run now</strong> to see what the job would change.
          </p>
        )
      )}
    </div>
  );
};

export default FollowSourceReportTab;
